import { last, pick, isEmpty } from 'lodash'

import PreOrdersItemViewModel from '../viewmodels/PreOrdersItemViewModel'

import { stringFloatToCents } from '../utils/formatters'
import { formatOrderDetails, makeClientPromotionParams } from '../utils/orderTotalUtils'

import PaymentTypes from '../utils/paymentTypes'

import { backendOrderModelKeys } from './order/attributes'
import { STADIUM_ORDER_STATES, isCreditType, isTabbed } from '../utils/orderStates'
import { groupTaxRules } from '../utils/totalUtils'

const COMPLETED_STATES = [
  STADIUM_ORDER_STATES.COMPLETED,
  STADIUM_ORDER_STATES.COMPLETION_PENDING,
  STADIUM_ORDER_STATES.APPROVED_OFFLINE,
  STADIUM_ORDER_STATES.CANCELLED,
]

const REFUNDED_STATES = [STADIUM_ORDER_STATES.REFUNDED, STADIUM_ORDER_STATES.PARTIALLY_REFUNDED]

// TODO - split payments?
const getPaymentData = (order) => {
  const paymentInfo = order?.paymentInfo ?? []

  return last(paymentInfo) || {}
}

const formatPaymentInfo = (order, paymentData) => {
  if (PaymentTypes.isCardRequired(order)) return 'Card Required'

  let { paymentType, shortDescription } = paymentData

  if (PaymentTypes.isMobileWalletPayment(paymentType)) shortDescription = shortDescription ?? 'Mobile Pay'

  if (PaymentTypes.isCash(paymentType)) return 'Cash'
  // NOTE (westinschepper): This is temporary
  if (PaymentTypes.isDirectBill(paymentType)) return 'Payment Pending'
  if (PaymentTypes.isHouseAccount(paymentType)) return 'House Account'
  if (PaymentTypes.isFnbCredit(paymentType)) return 'F&B Credit'

  if (shortDescription) return shortDescription

  return paymentType
}

const isRemotePayment = (paymentData) => {
  const { paymentType } = paymentData

  return PaymentTypes.isRemotePayment(paymentType)
}

export const separateLineItemsAndFees = (allLineItems = [], order) => {
  const lineItems = []
  const refundedServiceCharges = []
  let serviceChargeLineItem
  let taxLineItem
  let cashRoundingLineItem
  let refundCashRoundingLineItem

  allLineItems.forEach((li) => {
    if (!li.itemSpecialType && li.type !== 'CashRoundingLineItem') {
      lineItems.push(li)
      return
    }

    if (li.type === 'CashRoundingLineItem') {
      if (li.refunded || li.removedAt) {
        refundCashRoundingLineItem = li
        return
      }
      cashRoundingLineItem = li
      return
    }

    if (li.itemSpecialType === 'service_charge' || li.itemSpecialType === 'taxable_service_charge') {
      //Refunded service charges
      if (li.reason === 'Refunded Service Charge' || order.state === STADIUM_ORDER_STATES.CANCELLED) {
        refundedServiceCharges.push(li)
        return
      }

      //never push a removed service as the service charge for the order
      if (!li.removedAt) {
        serviceChargeLineItem = li
      }

      return
    }

    if (li.itemSpecialType === 'tax') {
      taxLineItem = li
      return
    }

    // fallback case
    li.modifiersString = '+' + li.modifiers[0]?.length + ' Modifiers'
  })

  return {
    lineItems,
    serviceChargeLineItem,
    taxLineItem,
    refundedServiceCharges,
    cashRoundingLineItem,
    refundCashRoundingLineItem,
  }
}

const parseOrderPricing = (order, lineItems, serviceChargeLineItem, taxLineItem, tipAmountInCents) => {
  const discountString = order?.discountAmountInCents ?? '0' // don't be fooled, it's a string
  const totalString = order?.total ?? '0.00'
  const resolvedLineItems = lineItems
  const scFraction = serviceChargeLineItem?.fractionalQuantity ?? 1

  if (serviceChargeLineItem !== undefined) {
    resolvedLineItems.push(serviceChargeLineItem)
  }

  return formatOrderDetails({
    discountAmountInCents: parseInt(discountString),
    lineItems: resolvedLineItems,
    promotions: order.promotions,
    serviceChargeInCents: scFraction * stringFloatToCents(serviceChargeLineItem?.price ?? '0.0'),
    taxAmountInCents: stringFloatToCents(taxLineItem?.price ?? '0.0'),
    tipAmountInCents,
    totalAmountInCents: stringFloatToCents(totalString),
    updatedAt: order.updatedAt,
  })
}

/**
 *
 * Whenever the payment has a token assigned we consider it's not an offline approval
 *
 * @param {*} userAttributes
 * @returns mapped user attributes
 */
export const updateOfflineApproval = (userAttributes = {}) => {
  if (!userAttributes.token) {
    return { ...userAttributes }
  }

  return {
    ...userAttributes,
    isOfflineApproval: false,
  }
}

export const mapPayments = (paymentInfo, orderNumber) => {
  //payment schema
  return {
    paymentId: paymentInfo.uuid,
    paymentType: paymentInfo.paymentType,
    amountInCents: paymentInfo.amountInCents,
    orderNumber,
    shortDescription: paymentInfo.shortDescription,
    userAttributes: isCreditType(paymentInfo.paymentType)
      ? updateOfflineApproval(paymentInfo.userAttributes)
      : paymentInfo.userAttributes,
    userName: '', //not present in stadium response
    cardType: '', //not present in stadium response
    label: '', //not present in stadium response
  }
}

const getBalanceDueIncents = (order) => {
  const safeOrderTotal = order?.total ?? ''
  const totalAmountInCents = parseInt(safeOrderTotal.replace('.', ''))

  if (COMPLETED_STATES.includes(order.state)) return 0

  if (REFUNDED_STATES.includes(order.state) && order.paymentSettledAt) return 0

  return totalAmountInCents
}

export const mapOrderDetails = (backendOrder = {}, items = {}, useTaxClasses = false) => {
  let order = pick(backendOrder, backendOrderModelKeys)

  const taxRulesBreakdown = {}
  const exemptableTaxRules = {}
  const refundedTaxRulesBreakdown = {}

  const isOrderReopened = backendOrder.reopened || false
  const payments = order.paymentInfo
    ?.filter((payment) => {
      // filter out zero amount payments when there are other payments present
      if (order.paymentInfo?.length <= 1) return true
      return payment.amountInCents !== 0
    })
    ?.map((payment) => mapPayments(payment, order.orderNumber))

  const {
    lineItems,
    serviceChargeLineItem,
    taxLineItem,
    refundedServiceCharges,
    cashRoundingLineItem,
    refundCashRoundingLineItem,
  } = separateLineItemsAndFees(order?.lineItems ?? [], order)

  const paymentData = getPaymentData(order)
  const safeOrderTotal = order?.total ?? ''
  const totalAmountInCents = parseInt(safeOrderTotal.replace('.', ''))
  const tipAmountInCents = stringFloatToCents(order?.tip ?? '0.00')
  const subtotalAmountInCents = stringFloatToCents(order?.subtotal ?? '0.00')
  const discountAmountInCents = stringFloatToCents(order?.discountAmountInCents)
  const serviceFee = serviceChargeLineItem
    ? {
        name: serviceChargeLineItem?.name,
        total: stringFloatToCents(serviceChargeLineItem?.price || '0.00'),
      }
    : undefined

  // The full JSON of the submission result does not need to be stored; only the order number.
  order.orderNumber = backendOrder?.orderSubmissionResult?.orderNumber ?? 'unknown'

  // Only a subset of the promotion is needed for follow-on totaling.
  order.promotions = makeClientPromotionParams(order.promotions)

  order.revenueName = order?.standName
  order.amountInCents = totalAmountInCents
  order.balanceDueInCents = getBalanceDueIncents(order)

  /**
   * Currently Stadium is returning the subtotal including the service fee so
   * we need to subtract it from the subtotalAmountInCents
   */
  order.subtotalAmountInCents = subtotalAmountInCents - (serviceFee?.total || 0)

  order.discountAmountInCents = discountAmountInCents
  order.tipAmountInCents = tipAmountInCents
  order.isOnline = true
  order.isRemotePayment = isRemotePayment(paymentData)
  order.isSyncing = false
  order.modifiedAt = order.updatedAt
  order.frozen = !!order.frozen
  order.paymentDisplay = formatPaymentInfo(order, paymentData)
  order.submittedAt = order.createdAt
  order.syncedAt = order.updatedAt
  order.customerPaymentId = paymentData.customerPaymentId
  order.wasCreatedInStadium = true
  order.serviceFee = serviceFee
  order.taxAmountInCents = stringFloatToCents(taxLineItem?.price || '0.00')
  order.reopened = isOrderReopened
  order.taxExempt = !!backendOrder.taxExempt

  //if we have some credit card payment let's update the user attributes to remove offline approval flag
  const isCreditOrder = payments?.some((p) => isCreditType(p.paymentType))

  if (backendOrder.userAttributes && isCreditOrder) {
    order.userAttributes = updateOfflineApproval(backendOrder.userAttributes)
  }

  if (isOrderReopened) {
    // preserve reopened flag locally
    order.wasReopened = true
  }

  if (!isOrderReopened) {
    //For non reopened orders we need to store all the payments in app state
    order.payments = payments
  } else {
    //For reopened orders we will store payments flagged with preservePaymentAfterReopen = true becasue these are needed for backbar orders
    const filteredPayments = payments.filter((payment) => payment.userAttributes?.preservePaymentAfterReopen)
    order.payments = filteredPayments

    //We need to reset the following order properties that are not synced between devices but some of them are needed
    order.orderState = ''
    order.paymentCompleted = false
    order.isRemotePayment = false
    order.creditCardAuthAdded = false
  }

  //filter non refunded line items refunded line items will be included in other refundedItemModels
  const nonRefundedLineItems = lineItems.filter((lineItem) => !lineItem.removedAt)

  if (!isEmpty(nonRefundedLineItems)) {
    order.itemModels = nonRefundedLineItems.map((lineItem) => {
      const itemModel = PreOrdersItemViewModel(lineItem)
      const item = items[lineItem.menuItemUuid]

      /* Adding the variant from the local items state since it's not returned from stadium...
      Fallback to an emtpy productSku in case it's not found so it will not crash the app in PPI processing */
      itemModel.variant = item?.variants?.length ? item.variants[0] : { productSku: '' }
      itemModel.discountAmountInCents = lineItem.discountAmountInCents

      if (useTaxClasses) {
        if (!order.taxExempt) {
          groupTaxRules(taxRulesBreakdown, lineItem)
        } else {
          groupTaxRules(exemptableTaxRules, lineItem)
        }
      }

      return itemModel
    })

    order.orderTotalFromOrder = parseOrderPricing(
      order,
      nonRefundedLineItems,
      serviceChargeLineItem,
      taxLineItem,
      tipAmountInCents,
    )
  } else {
    order.itemModels = []
  }

  if (isTabbed(order)) {
    order.tabName = order.userName
  }

  //refund totals
  order.refundedTip = parseFloat(backendOrder.refundedTip || 0).toFixed(2)
  order.refundedTotal = parseFloat(backendOrder.refundedTotal || 0).toFixed(2)
  order.refundedSubtotal = parseFloat(backendOrder.refundedSubtotal || 0).toFixed(2)
  order.refundedTax = parseFloat(backendOrder.refundedTax || 0).toFixed(2)

  const refundedServiceFeeTotal = refundedServiceCharges.reduce(
    (acc, refundedServiceCharge) => (acc += stringFloatToCents(refundedServiceCharge?.price || '0.00')),
    0,
  )

  if (refundedServiceFeeTotal) {
    order.refundedServiceFee = {
      name: refundedServiceCharges[0]?.name || '',
      total: refundedServiceFeeTotal,
    }
  }
  const refundedPayments = backendOrder.refundedPayments || []

  order.refundedPayments = refundedPayments.map((refundedPayment) => ({
    total: parseFloat(refundedPayment.total || 0).toFixed(2),
    description: refundedPayment.description,
  }))

  //Include refunded items and also include non refunded items that have modifiers refunded
  const refundedLineItems = lineItems.filter((lineItem) => {
    return lineItem.removedAt || lineItem.modifiers?.[0]?.some((modifier) => modifier.removedAt)
  })

  order.refundedItemModels = refundedLineItems.map((lineItem) => {
    const itemModel = PreOrdersItemViewModel(lineItem, true)
    itemModel.discountAmountInCents = lineItem.discountAmountInCents

    if (useTaxClasses && !order.taxExempt) {
      groupTaxRules(refundedTaxRulesBreakdown, lineItem, true)
    }

    return itemModel
  })

  //Include service charges taxes in the breakdown
  if (useTaxClasses) {
    if (!order.taxExempt) {
      //Add service charge taxes to the breakdown
      groupTaxRules(taxRulesBreakdown, serviceChargeLineItem)

      //Add refunded service charge taxes to the refunded breakdown
      refundedServiceCharges.forEach((refundedServiceCharge) => {
        groupTaxRules(refundedTaxRulesBreakdown, refundedServiceCharge, true)
      })
    } else {
      groupTaxRules(exemptableTaxRules, serviceChargeLineItem)
    }
  }

  order.taxRulesBreakdown = taxRulesBreakdown
  order.refundedTaxRulesBreakdown = refundedTaxRulesBreakdown
  order.exemptableTaxRules = exemptableTaxRules

  //Cash rounding updates
  if (cashRoundingLineItem) {
    order.cashRounded = true
    order.cashRoundingInCents = stringFloatToCents(cashRoundingLineItem.price)
  } else {
    order.cashRounded = false
    order.cashRoundingInCents = 0
  }

  if (refundCashRoundingLineItem) {
    order.refundCashRounded = true
    order.refundedCashRoundingInCents = stringFloatToCents(refundCashRoundingLineItem.price)
  } else {
    order.refundCashRounded = false
    order.refundedCashRoundingInCents = 0
  }

  return order
}

export const mapOrder = (backendOrder = {}) => {
  let order = pick(backendOrder, backendOrderModelKeys)
  const isOrderReopened = backendOrder.reopened || false
  const payments = order.paymentInfo?.map((payment) => mapPayments(payment, order.orderNumber))

  const paymentData = getPaymentData(order)
  const safeOrderTotal = order?.total ?? ''
  const totalAmountInCents = parseInt(safeOrderTotal.replace('.', ''))
  const balanceDueInCents = [
    STADIUM_ORDER_STATES.COMPLETED,
    STADIUM_ORDER_STATES.COMPLETION_PENDING,
    STADIUM_ORDER_STATES.APPROVED_OFFLINE,
    STADIUM_ORDER_STATES.CANCELLED,
  ].includes(order.state)
    ? 0
    : totalAmountInCents

  // The full JSON of the submission result does not need to be stored; only the order number.
  order.orderNumber = backendOrder?.orderSubmissionResult?.orderNumber ?? 'unknown'

  order.amountInCents = totalAmountInCents
  order.balanceDueInCents = balanceDueInCents
  order.isOnline = true
  order.isRemotePayment = isRemotePayment(paymentData)
  order.isSyncing = false
  order.modifiedAt = order.updatedAt
  order.paymentDisplay = formatPaymentInfo(order, paymentData)
  order.submittedAt = order.createdAt
  order.syncedAt = order.updatedAt
  order.customerPaymentId = paymentData.customerPaymentId
  order.wasCreatedInStadium = true
  order.reopened = isOrderReopened

  if (!isOrderReopened) {
    //For non reopened orders we need to store all the payments in app state
    order.payments = payments
  } else {
    //For reopened orders we will store payments flagged with preservePaymentAfterReopen = true becasue these are needed for backbar orders
    const filteredPayments = payments?.filter?.((payment) => payment.userAttributes?.preservePaymentAfterReopen)
    order.payments = filteredPayments || []

    //We need to reset the following order properties that are not synced between devices but some of them are needed
    order.orderState = ''
    order.paymentCompleted = false
    order.isRemotePayment = false
    order.creditCardAuthAdded = false

    // preserve reopened flag locally
    order.wasReopened = true
  }

  if (isTabbed(order)) {
    order.tabName = order.userName
  }

  return order
}
